import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  SimpleGrid,
  Box,
  Spinner,
  useToast,
  Icon,
  Divider,
  Grid,
  GridItem,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react';
import { FaShip, FaEdit, FaAnchor, FaBoxOpen, FaCalendarAlt, FaUsers } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getShipmentDetail, getShipmentContainers, computeStatusBucket } from '../services/shipment.service';
import { FCLExportShipment } from '../types/shipment.types';

interface ShipmentDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  shipmentId: string | number;
}

const ShipmentDetailsModal: React.FC<ShipmentDetailsModalProps> = ({ isOpen, onClose, shipmentId }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const initialFocusRef = useRef<HTMLButtonElement>(null);

  const [loading, setLoading] = useState(true);
  const [shipment, setShipment] = useState<FCLExportShipment | null>(null);
  const [containers, setContainers] = useState<any[]>([]);

  useEffect(() => {
    if (isOpen && shipmentId) loadShipmentDetails();
  }, [isOpen, shipmentId]);

  const loadShipmentDetails = async () => {
    setLoading(true);
    try {
      const [shipmentData, containersData] = await Promise.all([
        getShipmentDetail(shipmentId),
        getShipmentContainers(shipmentId),
      ]);
      setShipment(shipmentData);
      setContainers(containersData || []);
    } catch (error: any) {
      toast({ title: 'Error', description: 'Failed to load shipment details', status: 'error', duration: 5000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => { onClose(); navigate(`/bol/draft/${shipmentId}`); };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '—';
    const date = new Date(dateString.includes('T') ? dateString : `${dateString}T00:00:00`);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const isDraftDeadlinePassed = (): boolean => {
    if (!shipment?.deadline_draft) return false;
    const deadline = new Date(`${shipment.deadline_draft}T00:00:00`);
    deadline.setHours(23, 59, 59, 999);
    return new Date() > deadline;
  };

  const statusColor = (bucket: ReturnType<typeof computeStatusBucket>) => {
    switch (bucket) {
      case 'draft': return 'gray';
      case 'in_progress': return 'blue';
      case 'submitted': return 'yellow';
      case 'concluded': return 'green';
    }
  };

  const Field = ({ label, value }: { label: string; value?: string | null }) => (
    <Box>
      <Text fontSize="10px" fontWeight="600" color="appleTextSecondary" textTransform="uppercase" letterSpacing="wider" mb="2px">
        {label}
      </Text>
      <Text fontSize="sm" fontWeight="500" color="appleTextPrimary">{value || '—'}</Text>
    </Box>
  );

  const Section = ({ icon, title, children }: { icon: any; title: string; children: React.ReactNode }) => (
    <Box>
      <HStack spacing={2} mb={3}>
        <Icon as={icon} color="appleAccent" boxSize={3.5} />
        <Text fontSize="xs" fontWeight="700" color="appleTextSecondary" textTransform="uppercase" letterSpacing="wider">
          {title}
        </Text>
      </HStack>
      {children}
    </Box>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" initialFocusRef={initialFocusRef} scrollBehavior="inside" isCentered>
      <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(3px)" />
      <ModalContent maxH="90vh" bg="appleElevated" borderWidth="1px" borderColor="appleCardBorder" borderRadius="lg">

        {/* Header */}
        <ModalHeader borderBottom="1px solid" borderColor="appleCardBorder" pb={3}>
          <HStack justify="space-between" align="center" pr={8}>
            <HStack spacing={3}>
              <Box p={2} bg="appleAccent" borderRadius="sm">
                <Icon as={FaShip} color="white" boxSize={4} />
              </Box>
              <Box>
                <Text fontSize="lg" fontWeight="700" color="appleTextPrimary" lineHeight="1.2">
                  {loading ? 'Loading…' : (shipment?.shipment_number || 'Shipment Details')}
                </Text>
                {shipment?.booking && (
                  <Text fontSize="xs" color="appleTextSecondary">Booking: {shipment.booking}</Text>
                )}
              </Box>
            </HStack>
            {shipment && (
              <Badge
                colorScheme={statusColor(computeStatusBucket(shipment.shipment_status, shipment.due_status))}
                fontSize="xs"
                px={3} py={1}
                borderRadius="full"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                {shipment.shipment_status || 'N/A'}
              </Badge>
            )}
          </HStack>
        </ModalHeader>
        <ModalCloseButton top={4} />

        <ModalBody py={5} px={5}>
          {loading ? (
            <VStack py={12} spacing={3}>
              <Spinner size="xl" color="appleAccent" thickness="3px" />
              <Text color="appleTextSecondary" fontSize="sm">Loading shipment details…</Text>
            </VStack>
          ) : shipment ? (
            <VStack spacing={5} align="stretch" divider={<Divider borderColor="appleCardBorder" />}>

              {/* Parties */}
              <Section icon={FaUsers} title="Parties">
                <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
                  <GridItem>
                    <Field label="Shipper" value={shipment.shipper_name} />
                    {shipment.shipper_address && (
                      <Text fontSize="xs" color="appleTextSecondary" mt={1} whiteSpace="pre-line">
                        {shipment.shipper_address}
                      </Text>
                    )}
                  </GridItem>
                  <GridItem>
                    <Field label="Consignee" value={shipment.consignee_name} />
                    {shipment.consignee_address && (
                      <Text fontSize="xs" color="appleTextSecondary" mt={1} whiteSpace="pre-line">
                        {shipment.consignee_address}
                      </Text>
                    )}
                  </GridItem>
                </Grid>
              </Section>

              {/* Route */}
              <Section icon={FaAnchor} title="Route & Vessel">
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                  <Field label="Port of Loading" value={shipment.port_of_loading || shipment.pol} />
                  <Field label="Port of Discharge" value={shipment.port_of_discharge || shipment.pod} />
                  <Field label="Vessel" value={shipment.vessel} />
                  <Field label="Voyage" value={shipment.voyage} />
                  {shipment.place_of_receipt && <Field label="Place of Receipt" value={shipment.place_of_receipt} />}
                  {shipment.place_of_delivery && <Field label="Place of Delivery" value={shipment.place_of_delivery} />}
                </SimpleGrid>
              </Section>

              {/* Dates — hidden once the draft deadline has passed, it's no longer actionable */}
              {!isDraftDeadlinePassed() && (
                <Section icon={FaCalendarAlt} title="Dates">
                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                    <Field label="ETD" value={formatDate(shipment.etd)} />
                    <Field label="Draft Deadline" value={formatDate(shipment.deadline_draft)} />
                    {shipment.draft_date && <Field label="Draft Submitted" value={formatDate(shipment.draft_date)} />}
                    {shipment.deadline_draft_time && <Field label="Draft Deadline Time" value={shipment.deadline_draft_time} />}
                  </SimpleGrid>
                </Section>
              )}

              {/* Containers */}
              {containers.length > 0 && (
                <Section icon={FaBoxOpen} title={`Containers (${containers.length})`}>
                  <Box overflowX="auto">
                    <Table
                      size="sm"
                      variant="simple"
                      sx={{ 'th, td': { borderColor: 'appleCardBorder' } }}
                    >
                      <Thead>
                        <Tr>
                          {['Container #', 'Type', 'Tare', 'Gross', 'Net', 'CBM', 'Qty', 'Seal'].map(h => (
                            <Th key={h} fontSize="10px" px={2} py={1} color="appleTextSecondary">{h}</Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {containers.map((c, i) => (
                          <Tr key={c.id || i} _hover={{ bg: 'appleSurface' }}>
                            <Td px={2} py={1.5} fontSize="xs" fontWeight="600">{c.container_number || '—'}</Td>
                            <Td px={2} py={1.5} fontSize="xs">{c.container_type || '—'}</Td>
                            <Td px={2} py={1.5} fontSize="xs">{c.tare || '—'}</Td>
                            <Td px={2} py={1.5} fontSize="xs">{c.gross_weight || '—'}</Td>
                            <Td px={2} py={1.5} fontSize="xs">{c.net_weight || '—'}</Td>
                            <Td px={2} py={1.5} fontSize="xs">{c.cbm || '—'}</Td>
                            <Td px={2} py={1.5} fontSize="xs">{c.qty || '—'}</Td>
                            <Td px={2} py={1.5} fontSize="xs">{c.seal || '—'}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Section>
              )}

              {/* Cargo */}
              {(shipment.package_description || shipment.gross_weight || shipment.measurement || shipment.ncm_hs) && (
                <Section icon={FaBoxOpen} title="Cargo">
                  <VStack spacing={3} align="stretch">
                    {shipment.package_description && (
                      <Box>
                        <Text fontSize="10px" fontWeight="600" color="appleTextSecondary" textTransform="uppercase" letterSpacing="wider" mb="2px">
                          Description
                        </Text>
                        <Text fontSize="sm" color="appleTextPrimary" whiteSpace="pre-line">{shipment.package_description}</Text>
                      </Box>
                    )}
                    <SimpleGrid columns={{ base: 2, md: 3 }} spacing={4}>
                      {shipment.ncm_hs && <Field label="NCM / HS Code" value={shipment.ncm_hs} />}
                      {shipment.gross_weight && <Field label="Gross Weight" value={String(shipment.gross_weight)} />}
                      {shipment.measurement && <Field label="CBM" value={String(shipment.measurement)} />}
                    </SimpleGrid>
                  </VStack>
                </Section>
              )}

            </VStack>
          ) : (
            <Box textAlign="center" py={10}>
              <Text color="appleTextSecondary">No shipment data available</Text>
            </Box>
          )}
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="appleCardBorder" pt={3}>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={onClose} ref={initialFocusRef} size="sm">
              Close
            </Button>
            {shipment && !isDraftDeadlinePassed() && (
              <Button leftIcon={<Icon as={FaEdit} />} colorScheme="blue" onClick={handleEdit} size="sm">
                Edit Draft
              </Button>
            )}
          </HStack>
        </ModalFooter>

      </ModalContent>
    </Modal>
  );
};

export default ShipmentDetailsModal;
